import { z } from 'zod';
import { isoDate } from './primitives';

// Email validation
export const emailSchema = z.string().email('Invalid email address');

// Strong password requirements
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character (!@#$%^&*)')
  .refine((pwd) => !/(password|123456|admin)/i.test(pwd), 'Password is too common');

// User name - prevent XSS and SQL injection
export const userNameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(255, 'Name must be less than 255 characters')
  .trim()
  .regex(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes')
  .refine((name) => name.length > 0, 'Name cannot be empty');

// UUID validation
export const uuidSchema = z.string().uuid('Invalid ID format');

// Date validation (YYYY-MM-DD, real calendar date) — canonical primitive
export const dateSchema = isoDate;

// Timezone
export const timezoneSchema = z
  .string()
  .regex(/^[A-Za-z/_]+$/, 'Invalid timezone format')
  .default('America/Chicago');

// Frequency enum
export const frequencySchema = z.enum(['Daily', 'Weekly', 'Monthly', 'Annually', 'One-Off']);

// Month boolean array (12 months)
export const monthsSchema = z
  .array(z.boolean())
  .length(12, 'Must specify applicability for all 12 months');

// ============ USER SCHEMAS ============

export const createUserSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: userNameSchema,
  startDate: dateSchema.optional(),
}).strict();

export const updateUserSchema = z.object({
  name: userNameSchema,
}).strict();

// ============ TASK/task SCHEMAS ============

/**
 * tasks_one_off_due_date_check: a One-Off has a due date and nothing else does. Checked here so a
 * bad row is a 400 with a field error rather than a CHECK violation from the database.
 * Note: a refined object cannot be `.extend`ed/`.pick`ed — build a new schema if one is needed.
 */
function oneOffDueDateRule(task: { frequency: string; due_date?: string | null }, ctx: z.RefinementCtx) {
  const isOneOff = task.frequency === 'One-Off';
  if (isOneOff !== !!task.due_date) {
    ctx.addIssue({
      code: 'custom',
      path: ['due_date'],
      message: isOneOff ? 'One-Off tasks need a due date' : 'Only One-Off tasks have a due date',
    });
  }
}

export const createTaskSchema = z.object({
  name: z
    .string()
    .min(1, 'Task name is required')
    .max(255, 'Task name must be 255 characters or less')
    .trim(),
  frequency: frequencySchema,
  category_id: z.coerce.number().int().positive('Category must be a positive number'),
  job_role: z.string().min(1).max(100).trim().nullable().optional(),
  due_date: dateSchema.optional(),
  january: z.boolean().default(true),
  february: z.boolean().default(true),
  march: z.boolean().default(true),
  april: z.boolean().default(true),
  may: z.boolean().default(true),
  june: z.boolean().default(true),
  july: z.boolean().default(true),
  august: z.boolean().default(true),
  september: z.boolean().default(true),
  october: z.boolean().default(true),
  november: z.boolean().default(true),
  december: z.boolean().default(true),
  // Schedule config
  days_of_week: z.array(z.number().int().min(0).max(6)).nullable().optional(),
  day_of_month: z.number().int().min(-1).max(31).nullable().optional(),
  monthly_week: z.number().int().min(-1).max(5).nullable().optional(),
  annual_month: z.number().int().min(1).max(12).nullable().optional(),
  annual_day: z.number().int().min(1).max(31).nullable().optional(),
  ends_on: dateSchema.nullable().optional(),
}).strict().superRefine(oneOffDueDateRule);

export const updateTaskSchema = z.object({
  name: z
    .string()
    .min(1, 'Task name is required')
    .max(255, 'Task name must be 255 characters or less')
    .trim(),
  frequency: frequencySchema,
  category_id: z.coerce.number().int().positive('Category must be a positive number'),
  job_role: z.string().min(1).max(100).trim().nullable().optional(),
  due_date: dateSchema.nullable().optional(),
  january: z.boolean().optional(),
  february: z.boolean().optional(),
  march: z.boolean().optional(),
  april: z.boolean().optional(),
  may: z.boolean().optional(),
  june: z.boolean().optional(),
  july: z.boolean().optional(),
  august: z.boolean().optional(),
  september: z.boolean().optional(),
  october: z.boolean().optional(),
  november: z.boolean().optional(),
  december: z.boolean().optional(),
  // Schedule config
  days_of_week: z.array(z.number().int().min(0).max(6)).nullable().optional(),
  day_of_month: z.number().int().min(-1).max(31).nullable().optional(),
  monthly_week: z.number().int().min(-1).max(5).nullable().optional(),
  annual_month: z.number().int().min(1).max(12).nullable().optional(),
  annual_day: z.number().int().min(1).max(31).nullable().optional(),
  ends_on: dateSchema.nullable().optional(),
}).strict().superRefine(oneOffDueDateRule);

// New template task schema (with months array)
export const createTemplatetaskSchema = z.object({
  name: z
    .string()
    .min(1, 'task name is required')
    .max(255, 'Task name must be 255 characters or less')
    .trim(),
  frequency: frequencySchema,
  categoryId: z.coerce.number().int().positive('Category must be a positive number'),
  months: monthsSchema,
}).strict();

export const updateTemplatetaskSchema = createTemplatetaskSchema;

// ============ LOGIN SCHEMA ============

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
}).strict();

// ============ PASSWORD CHANGE SCHEMA ============

export const changePasswordSchema = z.object({
  newPassword: passwordSchema,
}).strict();

// Type exports for use in API routes
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type CreateTaskInput = z.infer<typeof createTaskSchema>;
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;
export type CreateTemplatetaskInput = z.infer<typeof createTemplatetaskSchema>;
export type UpdateTemplatetaskInput = z.infer<typeof updateTemplatetaskSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
